import {
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Image,
  Button,
  Row,
  Col,
  Tabs,
  Tooltip,
  DatePicker,
  Space,
} from "antd";
import moment from "moment";
import { toast } from "react-toastify";
import {
  animalCrudApi,
  animalExtendedApi,
  animalProcessCrudApi,
  birthCrudApi,
  milkRateCrudApi,
  seminationCrudApi,
  syncExtendedApi,
} from "../../client/EntityApi";
import DatePick from "../../core/component/DatePick";
import YesNoTag from "../../core/component/YesNoTag";
import {
  cellStyle,
  getConstantValue,
  getDateFromString,
  getDateString,
  getDateTimeString,
  getFullPath,
} from "../../core/helper/UtilHelper";
import { Rules } from "../../core/Rules";
import BaseFormList from "../BaseFormList";
import {
  getAnimalProcessFormItems,
  getAnimalProcessFormListItems,
  getPetVaccineFormItems,
  getPetVaccineFormListItems,
} from "./PetCPComponents";
import VaccineGroupSelect from "../VaccineGroupSelect";

const { TextArea } = Input;

export const getAnimalCPColumns = (
  paddocks: any[],
  animalStatus: any[],
  remainderState: string,
) => {
  return [
    {
      title: "Küpe No",
      dataIndex: "earringNo",
      key: "earringNo",
    },
    {
      title: "İşletme Id",
      dataIndex: "companyName",
      key: "companyName",
      sorter: (a: any, b: any) => a.companyName - b.companyName,
    },
    {
      title: "Yaş/Gün",
      dataIndex: "birthDay",
      key: "birthDay",
      sorter: (a: any, b: any) => a.birthDay - b.birthDay,
    },
    {
      title: "Padok",
      dataIndex: "paddock",
      key: "paddock",
      filters: paddocks,
      onFilter: (value: any, record: any) => record.paddock === value,
    },
    {
      title: "Son Tohumlama Tarihi",
      dataIndex: "lastSemination",
      key: "lastSemination",
      render: (value: any) =>
        value ? getDateString(value.date) : "Tohum Atılmadı",
    },
    {
      title: "Son Doğurma Tarihi",
      dataIndex: "lastBirth",
      key: "lastBirth",
      render: (value: any) =>
        value ? getDateString(value.date) : "Doğum Yapmadı",
    },
    {
      title: <Tooltip title="Sağılan Gün Sayısı">SGS</Tooltip>,
      dataIndex: "dsg",
      key: "dsg",
      sorter: (a: any, b: any) => a.dsg - b.dsg,
    },
    {
      title: <Tooltip title="Projeksiyon Sağılan Gün Sayısı">pSGS</Tooltip>,
      dataIndex: "sgs",
      key: "sgs",
      sorter: (a: any, b: any) => a.sgs - b.sgs,
    },
    {
      title: <Tooltip title="Günlük Süt Verimi">GSV</Tooltip>,
      dataIndex: "gsv",
      key: "gsv",
      sorter: (a: any, b: any) => a.gsv - b.gsv,
    },
    {
      title: <Tooltip title="Tohumlama Sayısı">TS</Tooltip>,
      dataIndex: "ts",
      key: "ts",
      sorter: (a: any, b: any) => a.ts - b.ts,
    },
    {
      title: (
        <Tooltip title="Tohumlamadan sonra geçen gün sayısı">TSGS</Tooltip>
      ),
      dataIndex: "pregnancyDay",
      key: "pregnancyDay",
      sorter: (a: any, b: any) => a.pregnancyDay - b.pregnancyDay,
    },
    {
      title: "Durum",
      dataIndex: "state",
      key: "state",
      filters: animalStatus,
      onFilter: (value: any, record: any) => record.state === value,
    },
    {
      title: "Kalan Süre",
      key: "remainderTime",
      sorter: (a: any, b: any) => a[remainderState] - b[remainderState],
      render: (value: any, row: any) => row[remainderState] ?? 0,
    },
    {
      title: "Sürüde",
      dataIndex: "isActive",
      key: "isActive",
      render: (value: any) => (
        <YesNoTag
          success={value}
          successText="Sürüde"
          failText="Sürüden Çıkarılmış"
        />
      ),
      defaultFilteredValue: [true],
      filters: [
        {
          text: "Sürüde",
          value: true,
        },
        {
          text: "Sürüden Çıkarılmış",
          value: false,
        },
      ],
      onFilter: (value: any, record: any) => record.isActive === value,
    },
  ];
};

export const getAnimalCPFormItems = (
  id: any,
  genders: any[],
  racials: any[],
  seminationStates: any[],
  birthTypes: any[],
  vaccines: any[],
  vaccineOptions: any[],
  vaccineStates: any[],
  diagnoses: any[],
) => {
  return (
    <>
      <Form.Item
        label="Küpe No"
        name="earringNo"
        rules={[Rules.required, Rules.max(255)]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="İşletme Id" name="companyName" rules={[Rules.max(255)]}>
        <Input />
      </Form.Item>
      <Form.Item label="Padok Adı" name="paddock" rules={[Rules.max(255)]}>
        <Input />
      </Form.Item>
      <Form.Item label="Ana Adı" name="mother" rules={[Rules.max(255)]}>
        <Input />
      </Form.Item>
      <Form.Item label="Baba Adı" name="father" rules={[Rules.max(255)]}>
        <Input />
      </Form.Item>
      <Form.Item
        label="Doğum Ağırlığı"
        name="birthWeight"
        initialValue="0"
        rules={[Rules.required]}
      >
        <InputNumber precision={3} min={0} stringMode />
      </Form.Item>
      <Form.Item
        label="Sütten Kesme Ağırlığı"
        name="weaningWeight"
        initialValue="0"
        rules={[Rules.required]}
      >
        <InputNumber precision={3} min={0} stringMode />
      </Form.Item>
      <Form.Item label="Cinsiyet" name="gender" rules={[Rules.required]}>
        <Select options={genders} />
      </Form.Item>
      <Form.Item label="Irk" name="racial" rules={[Rules.required]}>
        <Select options={racials} />
      </Form.Item>
      <Form.Item label="Doğum Tarihi" name="birthDate" rules={[Rules.required]}>
        <DatePick />
      </Form.Item>
      <Form.Item
        label="Sürüde mi?"
        name="isActive"
        initialValue={true}
        rules={[Rules.required]}
      >
        <Select
          options={[
            { label: "Sürüde", value: true },
            { label: "Sürüden Çıkarılmış", value: false },
          ]}
        />
      </Form.Item>
      <Form.Item
        label="Sürüden Çık. Sebebi"
        name="description"
        rules={[Rules.max(2000)]}
      >
        <TextArea
          placeholder="Sadece sürüden çıkartılan hayvanlar için açıklama giriniz. Sürüden çıkartılmamış bir hayvan için girilen açıklamalar yoksayılacaktır."
          rows={4}
        />
      </Form.Item>
      <Form.Item
        hidden
        name="company"
        rules={[Rules.required]}
        initialValue={"api/companies/" + id}
      >
        <Input />
      </Form.Item>
      <BaseFormList
        name="animalProcesses"
        addButtonText="Müdahele Ekle"
        formItems={(name) => getAnimalProcessFormListItems(name, diagnoses)}
      />
      <BaseFormList
        name="animalVaccineRecords"
        addButtonText="Aşı Kaydı Ekle"
        formItems={(name) =>
          getPetVaccineFormListItems(name, vaccineOptions, vaccineStates)
        }
      />
      <BaseFormList
        name="seminations"
        addButtonText="Tohum Ekle"
        title="Tohum Bilgileri"
        formItems={(name) => getSeminationForm(name, seminationStates)}
      />
      <BaseFormList
        name="births"
        addButtonText="Doğum Ekle"
        title="Doğum Bilgileri"
        formItems={(name) => getBirthForm(name, birthTypes)}
      />
      <BaseFormList
        name="milkRates"
        addButtonText="Süt Verisi Ekle"
        title="Süt Verisi"
        formItems={(name) => getMilRateForm(name)}
      />
      <BaseFormList
        name="syncs"
        addButtonText="Senkronizasyon Ekle"
        title="Senkronizasyon Bilgileri"
        formItems={(name) => getSyncForm(name)}
      />
      <BaseFormList
        name="individualVaccines"
        addButtonText="Bireysel Aşı Ekle"
        title="Bireysel Aşı Bilgileri"
        formItems={(name) => getIndividualVaccineForm(name, vaccines)}
      />
    </>
  );
};

export const extraProcessFormOnOk = (values: any, formName?: string) => {
  switch (formName) {
    case "usgForm":
      return seminationCrudApi.edit(values.id, values);
    case "syncForm":
      values.animals = values.animals.map((animalIri: string) => {
        return parseInt(animalIri.split("/")[2]);
      });
      return syncExtendedApi.startSync(values);
    case "milkRateForm":
      return milkRateCrudApi.create(values);
    case "birthForm":
      return birthCrudApi.create(values);
    case "seminationForm":
      return seminationCrudApi.create(values);
    case "individualVaccineForm":
      return animalExtendedApi.makeIndividualVaccine(values);
    case "syncCancelForm":
      return animalExtendedApi.syncCancel(values);
    case "animalProcessForm":
      return animalProcessCrudApi.create(values);
    case "animalVaccineForm":
      return animalExtendedApi.batchVaccineRecordCreate(values);
    case "earlyDryForm":
      return animalCrudApi.edit(values.animalId, values);
  }
};

export const getExtraProcessFormTitle = (formName?: string) => {
  switch (formName) {
    case "usgForm":
      return "Usg Gir";
    case "syncForm":
      return "Senkronizasyon Başlat";
    case "milkRateForm":
      return "Süt Verisi Gir";
    case "birthForm":
      return "Doğum Ekle";
    case "seminationForm":
      return "Tohum At";
    case "individualVaccineForm":
      return "Aşı Yap";
    case "syncCancelForm":
      return "Senkronizasyon İptal";
    case "animalProcessForm":
      return "Müdahale Kaydı Oluştur";
    case "animalVaccineForm":
      return "Aşı Kaydı";
    case "earlyDryForm":
      return "Erken Kuruya Alma Tarihi";
    default:
      return <></>;
  }
};

export const getExtraProcessFormItems = (
  formName?: string,
  formVariables?: any,
) => {
  switch (formName) {
    case "usgForm":
      return (
        <>
          <Form.Item label="USG1" name="usg1" initialValue="none">
            <Select options={formVariables.seminationStates} />
          </Form.Item>
          <Form.Item
            label="USG1'i Yapan"
            name="usg1Worker"
            rules={[Rules.max(255)]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="USG2" name="usg2" initialValue="none">
            <Select options={formVariables.seminationStates} />
          </Form.Item>
          <Form.Item
            label="USG2'i Yapan"
            name="usg2Worker"
            rules={[Rules.max(255)]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="id" rules={[Rules.required]}>
            <Input hidden />
          </Form.Item>
        </>
      );
    case "syncForm":
      return (
        <>
          <Form.Item label="Hayvan" name="animals" rules={[Rules.required]}>
            <Select
              showSearch
              mode="multiple"
              options={formVariables.mappedReadySyncAnimals}
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            />
          </Form.Item>
          <Form.Item
            label="Senkronizasyon Tipi"
            name="syncType"
            rules={[Rules.required]}
          >
            <Select options={formVariables.syncTypes} />
          </Form.Item>
          <Form.Item
            label="Başlangıç Tarihi"
            name="date"
            rules={[Rules.required]}
          >
            <DatePicker
              showTime
              placeholder="Tarih Seçiniz"
              format="DD/MM/YYYY HH:mm"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </>
      );
    case "milkRateForm":
      return (
        <>
          <Form.Item label="Hayvan" name="animal" rules={[Rules.required]}>
            <Select
              showSearch
              options={formVariables.mappedAnimals}
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            />
          </Form.Item>
          <Form.Item label="Veri" name="data" rules={[Rules.required]}>
            <InputNumber precision={1} min={0} stringMode />
          </Form.Item>
          <Form.Item label="Veri Tarihi" name="date" rules={[Rules.required]}>
            <DatePick />
          </Form.Item>
        </>
      );
    case "birthForm":
      return (
        <>
          <Form.Item
            label="Doğuran Hayvan"
            name="animal"
            rules={[Rules.required]}
          >
            <Select
              showSearch
              options={formVariables.mappedAnimals}
              onSelect={(_value: any, values: any) => {
                const date = formVariables.form.getFieldValue("date");
                const lastBirthDate = moment(values.lastBirth);
                if (date && lastBirthDate) {
                  const dayDiff = date.diff(lastBirthDate, "days");
                  if (dayDiff >= 0 && dayDiff < 250) {
                    formVariables.form.resetFields(["date"]);
                    toast.error(
                      "Son doğumun üzerinden henüz 250 gün geçmedi. Lütfen geçerli bir doğum tarihi giriniz.",
                    );
                  }
                }
              }}
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            />
          </Form.Item>
          <Form.Item label="Doğum Tipi" name="type" rules={[Rules.required]}>
            <Select
              options={formVariables.birthTypes}
              onChange={(value) => {
                if (
                  value &&
                  ["abort", "stillbirth"].includes(value as string)
                ) {
                  formVariables.form.resetFields(["births"]);
                }
              }}
            />
          </Form.Item>
          <Form.Item label="Doğum Tarihi" name="date" rules={[Rules.required]}>
            <DatePick
              onChange={(value: any) => {
                const animalIri = formVariables.form.getFieldValue("animal");
                if (animalIri) {
                  const findedAnimal = formVariables.mappedAnimals.find(
                    (animal: any) => {
                      return animal.value === animalIri;
                    },
                  );
                  if (findedAnimal && findedAnimal.lastBirth && value) {
                    const lastBirthDate = moment(findedAnimal.lastBirth);

                    const dayDiff = value.diff(lastBirthDate, "days");
                    if (dayDiff >= 0 && dayDiff < 250) {
                      toast.error(
                        "Son doğumun üzerinden henüz 250 gün geçmedi. Lütfen geçerli bir doğum tarihi giriniz.",
                      );
                      formVariables.form.resetFields(["date"]);
                    }
                  }
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label="Açıklama"
            name="description"
            rules={[Rules.max(1000)]}
          >
            <TextArea rows={4} />
          </Form.Item>
          <BaseFormList
            name="births"
            title="Yavru Bilgileri"
            addButtonText="Yavru Ekle"
            onClick={(add) => {
              const formValues = formVariables.form.getFieldsValue();
              [undefined, "abort", "stillbirth"].includes(formValues.type)
                ? toast.error("Girilen doğum tipi için yavru eklenemez.")
                : add({
                    birthWeight: "0",
                    weaningWeight: "0",
                    isActive: true,
                  });
            }}
            formItems={(name) => (
              <>
                <Form.Item
                  label="İşletme Id"
                  name={[name, "companyName"]}
                  rules={[Rules.max(255)]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Padok Adı"
                  name={[name, "paddock"]}
                  rules={[Rules.max(255)]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Doğum Ağırlığı"
                  name={[name, "birthWeight"]}
                  rules={[Rules.required]}
                >
                  <InputNumber precision={3} min={0} stringMode />
                </Form.Item>
                <Form.Item
                  label="Sütten Kesme Ağırlığı"
                  name={[name, "weaningWeight"]}
                  rules={[Rules.required]}
                >
                  <InputNumber precision={3} min={0} stringMode />
                </Form.Item>
                <Form.Item
                  label="Cinsiyet"
                  name={[name, "gender"]}
                  rules={[Rules.required]}
                >
                  <Select options={formVariables.genders} />
                </Form.Item>
                <Form.Item
                  label="Irk"
                  name={[name, "racial"]}
                  rules={[Rules.required]}
                >
                  <Select options={formVariables.racials} />
                </Form.Item>
                <Form.Item
                  label="Sürüde mi?"
                  name={[name, "isActive"]}
                  initialValue={true}
                  rules={[Rules.required]}
                >
                  <Select
                    options={[
                      { label: "Sürüde", value: true },
                      { label: "Sürüden Çıkarılmış", value: false },
                    ]}
                  />
                </Form.Item>
              </>
            )}
          />
        </>
      );
    case "seminationForm":
      return (
        <>
          <Form.Item
            label="Tohumlanacak Olan"
            name="animal"
            rules={[Rules.required]}
          >
            <Select
              showSearch
              options={formVariables.mappedAnimals}
              onSelect={(_value: any, values: any) => {
                if (values.lastSeminationState === "pregnant") {
                  formVariables.form.resetFields(["animal"]);
                  toast.error(
                    "Gebe hayvana tohum atılamaz, lütfen gebe olmayan bir hayvan seçiniz.",
                  );
                  return;
                }
                const date = formVariables.form.getFieldValue("date");
                const lastBirthDate = moment(values.lastBirth);
                if (date && lastBirthDate) {
                  const dayDiff = date.diff(lastBirthDate, "days");
                  if (dayDiff >= 0 && dayDiff < 15) {
                    formVariables.form.resetFields(["date"]);
                    toast.error(
                      "Girilen tohumlama tarihi son doğum tarihinden en az 15 gün büyük olmalıdır.",
                    );
                  }
                }
              }}
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            />
          </Form.Item>
          <Form.Item
            label="Sperma"
            name="sperma"
            rules={[Rules.required, Rules.max(255)]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Tohum Tarihi" name="date" rules={[Rules.required]}>
            <DatePick
              onChange={(value: any) => {
                const animalIri = formVariables.form.getFieldValue("animal");
                if (animalIri) {
                  const findedAnimal = formVariables.mappedAnimals.find(
                    (animal: any) => animal.value === animalIri,
                  );
                  if (findedAnimal && findedAnimal.lastBirth && value) {
                    const lastBirthDate = moment(findedAnimal.lastBirth);
                    const dayDiff = value.diff(lastBirthDate, "days");
                    if (dayDiff >= 0 && dayDiff < 15) {
                      toast.error(
                        "Girilen tohumlama tarihi son doğum tarihinden en az 15 gün büyük olmalıdır.",
                      );
                      formVariables.form.resetFields(["date"]);
                    }
                  }
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label="Tohum Atan"
            name="seminationWorker"
            rules={[Rules.max(255)]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="USG1" name="usg1" initialValue="none">
            <Select options={formVariables.seminationStates} />
          </Form.Item>
          <Form.Item
            label="USG1'i Yapan"
            name="usg1Worker"
            rules={[Rules.max(255)]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="USG2" name="usg2" initialValue="none">
            <Select options={formVariables.seminationStates} />
          </Form.Item>
          <Form.Item
            label="USG2'i Yapan"
            name="usg2Worker"
            rules={[Rules.max(255)]}
          >
            <Input />
          </Form.Item>
        </>
      );
    case "individualVaccineForm":
      return (
        <>
          <Form.Item
            label="Aşılanacak Olanlar"
            name="animals"
            rules={[Rules.required]}
          >
            <Select
              showSearch
              mode="multiple"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={formVariables.mappedReadyVaccineAnimals}
            />
          </Form.Item>
          <Form.Item
            label="Aşı Tipi"
            name="vaccineName"
            rules={[Rules.required]}
          >
            <Select showSearch options={formVariables.mappedVaccines} />
          </Form.Item>
          <Form.Item
            label="Yapılma Tarihi"
            name="date"
            rules={[Rules.required]}
          >
            <DatePick />
          </Form.Item>
          <Form.Item
            label="Açıklama"
            name="description"
            rules={[Rules.required, Rules.max(500)]}
          >
            <TextArea rows={4} />
          </Form.Item>
        </>
      );
    case "syncCancelForm":
      return (
        <>
          <Form.Item
            label="Senkronizasyonu İptal Edilecekler"
            name="ids"
            rules={[Rules.required]}
          >
            <Select
              showSearch
              mode="multiple"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={formVariables.mappedInSyncAnimals}
            />
          </Form.Item>
        </>
      );
    case "animalProcessForm":
      return (
        <>
          <Form.Item label="Hayvan" name="animal" rules={[Rules.required]}>
            <Select options={formVariables.mappedAnimals} />
          </Form.Item>
          {getAnimalProcessFormItems(formVariables.diagnoses)}
        </>
      );
    case "animalVaccineForm":
      return (
        <>
          <Tabs>
            <Tabs.TabPane tab="Oluştur" key="create">
              <>
                <Form.Item
                  label="Aşı Grubu"
                  name="groupName"
                  rules={[Rules.required]}
                  tooltip="Aşı grubu aşıyı daha sonra düzenlerken kullanabilmek için gereken bir belirteçtir."
                >
                  <Input placeholder="Aşı grup ismi giriniz" />
                </Form.Item>
              </>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Düzenle" key="edit">
              <>
                <Form.Item
                  label="Aşı Grubu"
                  name="groupName"
                  rules={[Rules.required]}
                >
                  <VaccineGroupSelect
                    companyId={formVariables?.company?.id}
                    onSelect={(_: any, option: any) =>
                      formVariables.form.setFieldsValue({
                        ...option.entity,
                        date: getDateFromString(option.entity.date),
                      })
                    }
                  />
                </Form.Item>
              </>
            </Tabs.TabPane>
          </Tabs>
          <Form.Item
            style={{ marginBottom: "8px" }}
            label="Hayvan"
            name="animals"
            rules={[Rules.required]}
          >
            <Select mode="multiple" options={formVariables.mappedAnimals} />
          </Form.Item>
          <Row
            style={{ marginBottom: 8 }}
            justify="space-between"
            gutter={[8, 8]}
          >
            <Col span={12}>
              <Button
                block
                type="primary"
                onClick={() =>
                  formVariables.form.setFieldsValue({
                    animals: formVariables.mappedAnimals.map(
                      (mappedAnimal: any) => mappedAnimal.value,
                    ),
                  })
                }
              >
                Hepsini Seç
              </Button>
            </Col>
            <Col span={12}>
              <Button
                block
                danger
                type="primary"
                onClick={() =>
                  formVariables.form.setFieldsValue({ animals: [] })
                }
              >
                Hepsini Kaldır
              </Button>
            </Col>
          </Row>

          {getPetVaccineFormItems(
            formVariables.vaccineOptions,
            formVariables.mappedVaccineStatus,
          )}
        </>
      );
    case "earlyDryForm":
      return (
        <>
          <Form.Item hidden name="animalId" rules={[Rules.required]}>
            <Input />
          </Form.Item>
          <Form.Item label="Erken Kuruya Alınma Tarihi" name="dryDate">
            <DatePick />
          </Form.Item>
        </>
      );
    default:
      return <></>;
  }
};

export const getAnimalCPExpandedRowRender = (
  record: any,
  genders: any[],
  racials: any[],
  seminationStates: any[],
  birthTypes: any[],
  vaccineStates: any[],
  syncTypes: any[],
) => {
  const boxStyle = {
    border: "1px solid grey",
    padding: "5px",
    margin: "5px 0",
  };

  const renderSyncStepCell = (label: string, value: any, span: number = 2, isDate: boolean = true) => {
    const dateTime = value ? getDateTimeString(value).split(" ") : ["-"];
    return (
      <Col span={span}>
        <Space size={1} direction="vertical" align="center" style={{textAlign: "center"}}>
          <div>
            <b>{label}</b>
          </div>
          {isDate
            ? dateTime.map((item, i) => <div key={i}>{item}</div>)
            : value}
        </Space>
      </Col>
    );
  };

  return (
    <>
      <table style={{ minWidth: "100%", marginBottom: "1em" }}>
        <thead>
          <tr>
            <th style={cellStyle}>Yaş (Gün)</th>
            <th style={cellStyle}>Yaş (Ay)</th>
            <th style={cellStyle}>Yaş (Yıl)</th>
            <th style={cellStyle}>Doğum Tarihi</th>
            <th style={cellStyle}>Cinsiyet</th>
            <th style={cellStyle}>Irk</th>
            <th style={cellStyle}>Padok</th>
            <th style={cellStyle}>Ana Adı</th>
            <th style={cellStyle}>Baba Adı</th>
            <th style={cellStyle}>Doğum Ağırlığı</th>
            <th style={cellStyle}>Sütten Kesme Ağırlığı</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th style={cellStyle}>{record.birthDay}</th>
            <th style={cellStyle}>{record.birthMonth}</th>
            <th style={cellStyle}>{record.birthYear}</th>
            <th style={cellStyle}>{getDateString(record.birthDate)}</th>
            <th style={cellStyle}>
              {getConstantValue(record.gender, genders)}
            </th>
            <th style={cellStyle}>
              {getConstantValue(record.racial, racials)}
            </th>
            <th style={cellStyle}>{record.paddock}</th>
            <th style={cellStyle}>{record.mother}</th>
            <th style={cellStyle}>{record.father}</th>
            <th style={cellStyle}>{record.birthWeight}</th>
            <th style={cellStyle}>{record.weaningWeight}</th>
          </tr>
        </tbody>
      </table>
      <table
        style={{
          width: "50%",
          textAlign: "center",
          borderCollapse: "collapse",
          border: "1px solid black",
          margin: "1em 0px 1em 0px",
        }}
      >
        <thead>
          <tr style={cellStyle}>
            <th style={cellStyle} colSpan={7}>
              Tohum Bilgileri ({record.seminations.length} Defa Tohumlandı)
            </th>
          </tr>
        </thead>
        <tbody>
          <tr style={cellStyle}>
            <th style={cellStyle}>Sperma</th>
            <th style={cellStyle}>Tohum Atan</th>
            <th style={cellStyle}>USG1</th>
            <th style={cellStyle}>USG1'i Yapan</th>
            <th style={cellStyle}>USG2</th>
            <th style={cellStyle}>USG2'i Yapan</th>
            <th style={cellStyle}>Tohum Tarihi</th>
          </tr>
          {record.seminations.map((semination: any, index: number) => {
            return (
              <tr style={cellStyle} key={index}>
                <th style={cellStyle}>{semination.sperma}</th>
                <th style={cellStyle}>
                  {semination.seminationWorker
                    ? semination.seminationWorker
                    : "BELİRSİZ"}
                </th>
                <th style={cellStyle}>
                  {getConstantValue(semination.usg1, seminationStates)}
                </th>
                <th style={cellStyle}>{semination.usg1Worker}</th>
                <th style={cellStyle}>
                  {getConstantValue(semination.usg2, seminationStates)}
                </th>
                <th style={cellStyle}>{semination.usg2Worker}</th>
                <th style={cellStyle}>{getDateString(semination.date)}</th>
              </tr>
            );
          })}
        </tbody>
      </table>
      <table
        style={{
          width: "50%",
          textAlign: "center",
          borderCollapse: "collapse",
          border: "1px solid black",
          margin: "1em 0px 1em 0px",
        }}
      >
        <thead>
          <tr style={cellStyle}>
            <th style={cellStyle} colSpan={5}>
              Doğum Bilgileri ({record.births.length} Defa Doğurdu)
            </th>
          </tr>
        </thead>
        <tbody>
          <tr style={cellStyle}>
            <th style={cellStyle}>Doğum Tipi</th>
            <th style={cellStyle}>Açıklaması</th>
            <th style={cellStyle}>Doğurma Tarihi</th>
          </tr>
          {record.births.map((birth: any, index: number) => {
            return (
              <tr style={cellStyle} key={index}>
                <th style={cellStyle}>
                  {getConstantValue(birth.type, birthTypes)}
                </th>
                <th style={cellStyle}>{birth.description}</th>
                <th style={cellStyle}>{getDateString(birth.date)}</th>
              </tr>
            );
          })}
        </tbody>
      </table>
      <table
        style={{
          width: "50%",
          textAlign: "center",
          borderCollapse: "collapse",
          border: "1px solid black",
          margin: "1em 0px 1em 0px",
        }}
      >
        <thead>
          <tr style={cellStyle}>
            <th style={cellStyle} colSpan={5}>
              Süt Bilgileri ({record.milkRates.length} Kayıt Mevcut)
            </th>
          </tr>
        </thead>
        <tbody>
          <tr style={cellStyle}>
            <th style={cellStyle}>Veri</th>
            <th style={cellStyle}>Tarih</th>
          </tr>
          {record.milkRates.map((milkRate: any, index: number) => {
            return (
              <tr style={cellStyle} key={index}>
                <th style={cellStyle}>{milkRate.data}</th>
                <th style={cellStyle}>{getDateString(milkRate.date)}</th>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ width: "90%", border: "1px solid black", padding:16 }}>
        <Row justify="center" style={{ width: "100%", marginBottom: 16 }}>
          <b>Senkronizasyon Bilgileri ({record.syncs.length} Kayıt Mevcut)</b>
        </Row>
        {record.syncs.map((sync: any, index: number) => {
          switch (sync.syncType) {
            case "ovsync48":
            case "ovsync56":
              return (
                <>
                  <Row key={index} style={{ width: "100%" }} gutter={[16, 16]}>
                    {renderSyncStepCell("Protokol", sync.syncType, 2, false)}
                    {renderSyncStepCell("GnRH (Başlangıç)", sync.gnrh, 2)}
                    {renderSyncStepCell("PG", sync.pg, 2)}
                    {renderSyncStepCell("GnRH2", sync.gnrh2, 2)}
                    {renderSyncStepCell("Tohumlama", sync.semination, 2)}
                    {renderSyncStepCell("Senk. Bittiği Tarih", sync.passiveDate, 3)}
                    {renderSyncStepCell("Senk. Bitiş Şekli", sync.type == null ? "-" : sync.type ? "Tohumlama" : "İptal", 3, false)}
                  </Row>
                  <Divider style={{ margin: 4 }} />
                </>
              );
            case "doubleSync":
              return (
                <>
                  <Row key={index} style={{ width: "100%" }} gutter={[16, 16]}>
                    {renderSyncStepCell("Protokol", sync.syncType, 2, false)}
                    {renderSyncStepCell("PG (Başlangıç)", sync.pg, 2)}
                    {renderSyncStepCell("GnRH", sync.gnrh, 2)}
                    {renderSyncStepCell("PG2", sync.pg2, 2)}
                    {renderSyncStepCell("GnRH2", sync.gnrh2, 2)}
                    {renderSyncStepCell("Tohumlama", sync.semination, 2)}
                    {renderSyncStepCell("Senk. Bittiği Tarih", sync.passiveDate, 3)}
                    {renderSyncStepCell("Senk. Bitiş Şekli", sync.type == null ? "-" : sync.type ? "Tohumlama" : "İptal", 3, false)}
                  </Row>
                  <Divider style={{ margin: 4 }} />
                </>
              );
            case "doubleOvsync":
              return (
                <>
                  <Row key={index} style={{ width: "100%" }} gutter={[16, 16]}>
                    {renderSyncStepCell("Protokol", sync.syncType, 2, false)}
                    {renderSyncStepCell("GnRH (Başlangıç)", sync.gnrh, 2)}
                    {renderSyncStepCell("PG", sync.pg, 2)}
                    {renderSyncStepCell("GnRH2", sync.gnrh2, 2)}
                    {renderSyncStepCell("GnRH3", sync.gnrh3, 2)}
                    {renderSyncStepCell("PG2", sync.pg2, 2)}
                    {renderSyncStepCell("PG3", sync.pg3, 2)}
                    {renderSyncStepCell("GnRH4", sync.gnrh4, 2)}
                    {renderSyncStepCell("Tohumlama", sync.semination, 2)}
                    {renderSyncStepCell("Senk. Bittiği Tarih", sync.passiveDate, 3)}
                    {renderSyncStepCell("Senk. Bitiş Şekli", sync.type == null ? "-" : sync.type ? "Tohumlama" : "İptal", 3, false)}
                  </Row>
                  <Divider style={{ margin: 4 }} />
                </>
              );
            case "g6g":
              return (
                <>
                  <Row key={index} style={{ width: "100%" }} gutter={[16, 16]}>
                    {renderSyncStepCell("Protokol", sync.syncType, 2, false)}
                    {renderSyncStepCell("PG (Başlangıç)", sync.pg, 2)}
                    {renderSyncStepCell("GnRH", sync.gnrh, 2)}
                    {renderSyncStepCell("GnRH2", sync.gnrh2, 2)}
                    {renderSyncStepCell("PG2", sync.pg2, 2)}
                    {renderSyncStepCell("GnRH3", sync.gnrh3, 2)}
                    {renderSyncStepCell("Tohumlama", sync.semination, 2)}
                    {renderSyncStepCell("Senk. Bittiği Tarih", sync.passiveDate, 2)}
                    {renderSyncStepCell("Senk. Bitiş Şekli", sync.type == null ? "-" : sync.type ? "Tohumlama" : "İptal", 2, false)}
                  </Row>
                  <Divider style={{ margin: 4 }} />
                </>
              );
            default:
              return <></>;
          }
        })}
      </div>
      <table
        style={{
          width: "50%",
          textAlign: "center",
          borderCollapse: "collapse",
          border: "1px solid black",
          margin: "1em 0px 1em 0px",
        }}
      >
        <thead>
          <tr style={cellStyle}>
            <th style={cellStyle} colSpan={5}>
              Bireysel Aşı Bilgileri ({record.individualVaccines.length} Kayıt
              Mevcut)
            </th>
          </tr>
        </thead>
        <tbody>
          <tr style={cellStyle}>
            <th style={cellStyle}>Aşı İsmi</th>
            <th style={cellStyle}>Açıklaması</th>
            <th style={cellStyle}>Yapılma Tarihi</th>
          </tr>
          {record.individualVaccines.map(
            (individualVaccine: any, index: number) => {
              return (
                <tr style={cellStyle} key={index}>
                  <th style={cellStyle}>{individualVaccine.name}</th>
                  <th style={cellStyle}>{individualVaccine.description}</th>
                  <th style={cellStyle}>
                    {individualVaccine.date
                      ? getDateString(individualVaccine.date)
                      : "-"}
                  </th>
                </tr>
              );
            },
          )}
        </tbody>
      </table>
      <div style={boxStyle}>
        <h3>
          <b>Aşı Listesi ({record.animalVaccineRecords.length + " Adet"})</b>
        </h3>
        <ul>
          {record.animalVaccineRecords.map(
            (animalVaccineRecord: any, index: number) => (
              <li key={index}>
                {animalVaccineRecord.vaccine.name} |{" "}
                {getDateString(animalVaccineRecord.date)} |{" "}
                {getConstantValue(animalVaccineRecord.state, vaccineStates)}
              </li>
            ),
          )}
        </ul>
      </div>
      <div style={boxStyle}>
        <h3>
          <b>Müdahale Listesi ({record.animalProcesses.length + " Adet"})</b>
          <Divider />
          {record.animalProcesses.map((animalProcess: any, index: number) => (
            <div key={index}>
              <p>
                <b>Şüphe</b> : {animalProcess.suspicion ?? "-"}
              </p>
              <p>
                <b>Teşhis</b> : {animalProcess.diagnosis ?? "-"}
              </p>
              <p>
                <b>Tedavi</b> : {animalProcess.cure ?? "-"}
              </p>
              <p>
                <b>Tarih</b> : {getDateString(animalProcess.createdAt)}
              </p>
              {animalProcess.media && (
                <Image width={200} src={getFullPath(animalProcess.media.uid)} />
              )}
              <Divider />
            </div>
          ))}
        </h3>
      </div>
      {record.description && (
        <p style={{ color: "red" }}>
          <b>Sürüden Çıkartılma Sebebi</b> : {record.description}
        </p>
      )}
    </>
  );
};

const getIndividualVaccineForm = (name: any, vaccines: any[]) => {
  return (
    <>
      <Form.Item
        label="Aşı Tipi"
        name={[name, "name"]}
        rules={[Rules.required]}
      >
        <Select options={vaccines} />
      </Form.Item>
      <Form.Item
        label="Yapılma Tarihi"
        name={[name, "date"]}
        rules={[Rules.required]}
      >
        <DatePick />
      </Form.Item>
      <Form.Item
        label="Açıklama"
        name={[name, "description"]}
        rules={[Rules.required, Rules.max(500)]}
      >
        <TextArea rows={4} />
      </Form.Item>
    </>
  );
};

const getSyncForm = (name: any) => {
  return (
    <>
      <Form.Item
        label="Başlangıç Tarihi"
        name={[name, "date"]}
        rules={[Rules.required]}
      >
        <DatePick />
      </Form.Item>
    </>
  );
};

const getMilRateForm = (name: any) => {
  return (
    <>
      <Form.Item label="Veri" name={[name, "data"]} rules={[Rules.required]}>
        <InputNumber precision={1} min={0} stringMode />
      </Form.Item>
      <Form.Item label="Tarih" name={[name, "date"]} rules={[Rules.required]}>
        <DatePick />
      </Form.Item>
    </>
  );
};

const getBirthForm = (name: any, birthTypes: any[]) => {
  return (
    <>
      <Form.Item
        label="Doğum Tipi"
        name={[name, "type"]}
        rules={[Rules.required]}
      >
        <Select options={birthTypes} />
      </Form.Item>
      <Form.Item
        label="Doğum Tarihi"
        name={[name, "date"]}
        rules={[Rules.required]}
      >
        <DatePick />
      </Form.Item>
      <Form.Item
        label="Açıklama"
        name={[name, "description"]}
        rules={[Rules.max(1000)]}
      >
        <TextArea rows={4} />
      </Form.Item>
    </>
  );
};

const getSeminationForm = (name: any, seminationStates: any[]) => {
  return (
    <>
      <Form.Item
        label="Sperma"
        name={[name, "sperma"]}
        rules={[Rules.required, Rules.max(255)]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Tohum Tarihi"
        name={[name, "date"]}
        rules={[Rules.required]}
      >
        <DatePick />
      </Form.Item>
      <Form.Item
        label="Tohum Atan"
        name={[name, "seminationWorker"]}
        rules={[Rules.max(255)]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="USG1" name={[name, "usg1"]}>
        <Select options={seminationStates} />
      </Form.Item>
      <Form.Item
        label="USG1'i Yapan"
        name={[name, "usg1Worker"]}
        rules={[Rules.max(255)]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="USG2" name={[name, "usg2"]}>
        <Select options={seminationStates} />
      </Form.Item>
      <Form.Item
        label="USG2'i Yapan"
        name={[name, "usg2Worker"]}
        rules={[Rules.max(255)]}
      >
        <Input />
      </Form.Item>
    </>
  );
};
